import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
// Following the ESLINT rule here will actually cause recaptcha not to render
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from "react-google-recaptcha";

import {
  useListContactCategoriesQuery,
  useSubmitContactFormMutation,
} from "api/public/contactUs/contactUs.api";
import { useAppForm } from "components/Forms/AppForm";

import { contactFormSchema } from "./Form/ContactUs.schema";
import { contactUsDefaultValues } from "./Form/ContactUsDefaultValues";

const MAX_REQ_LENGTH = 2000;
const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

interface FormContentProps {
  onSubmitted?: (_ticket: string) => void;
  hidden?: boolean;
}

const FormContent = ({ onSubmitted, hidden = false }: FormContentProps) => {
  const [error, setError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { data: categories = [], isLoading: categoriesLoading } =
    useListContactCategoriesQuery();

  const [submitForm, { isLoading: isSubmitting }] =
    useSubmitContactFormMutation();

  const categoryOptions = categories.map((cat) => ({
    value: String(cat.cid),
    label: cat.cat,
  }));

  const form = useAppForm({
    defaultValues: contactUsDefaultValues,
    validators: {
      onSubmit: contactFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError("");

      if (!recaptchaToken) {
        setError("Please complete the reCAPTCHA");
        return;
      }

      try {
        const result = await submitForm({
          custuname: value.custuname,
          custemail: value.custemail,
          custorg: value.custorg,
          phone1: value.phone1 || undefined,
          cid: Number(value.cid),
          req: value.req,
          recaptchaToken,
        }).unwrap();

        onSubmitted?.(result.ticket);
      } catch {
        setError("Failed to send message. Please try again.");
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    },
    onSubmitInvalid: () => {
      setError("Please fill out all required fields before submitting");
    },
  });

  const handleReset = () => {
    form.reset();
    setError("");
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  // Reset form state when hidden (modal closed or phase changed away)
  useEffect(() => {
    if (hidden) {
      handleReset();
    }
  }, [hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  if (categoriesLoading && !hidden) {
    return (
      <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </DialogContent>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      hidden={hidden}
    >
      <DialogContent sx={{ paddingTop: 0 }}>
        <Typography variant="body2">
          Please use the form below to send comments or suggestions to
          NIAP/CCEVS.
        </Typography>
        <Typography variant="body2" mb={2}>
          Your contact information is optional unless you desire a response.
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={2}
        >
          {/* Left column */}
          <Stack spacing={2}>
            <form.AppField name="custuname">
              {(field) => <field.TextInput label="Name" required />}
            </form.AppField>

            <form.AppField name="custemail">
              {(field) => <field.TextInput label="Email Address" required />}
            </form.AppField>

            <form.AppField name="custorg">
              {(field) => <field.TextInput label="Organization" required />}
            </form.AppField>

            <form.AppField name="phone1">
              {(field) => (
                <field.TextInput
                  label="Telephone Number"
                  helperText="Specify country code, DSN, comm, etc, as applicable"
                />
              )}
            </form.AppField>
          </Stack>

          {/* Right column */}
          <Stack spacing={2}>
            <form.AppField name="cid">
              {(field) => (
                <field.SelectField
                  label="Category"
                  options={categoryOptions}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="req">
              {(field) => (
                <field.TextInput
                  label="Remarks, Comments, Questions"
                  required
                  multiline
                  rows={6}
                  slotProps={{
                    htmlInput: { maxLength: MAX_REQ_LENGTH },
                  }}
                  helperText={`Please limit to 2000 characters: ${field.state.value?.length ?? 0} / ${MAX_REQ_LENGTH}`}
                />
              )}
            </form.AppField>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, alignItems: "flex-end" }}>
        <Box flex={1}>
          {/* @ts-ignore types are incompatible with React 18 */}
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            onChange={(token: string | null) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
        </Box>

        <Button type="submit" variant="contained" disabled={isSubmitting}>
          Submit
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={isSubmitting}
        >
          Reset
        </Button>
      </DialogActions>
    </form>
  );
};

export default FormContent;
